import React, { useState, useEffect } from 'react';
import translations from '../i18n/translations';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Avatar,
  InputAdornment,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Select,
  MenuItem,
  Checkbox,
  Autocomplete  // Add this import
} from '@mui/material';
import axios from 'axios';
import {
  ShoppingCart,
  Person,
  LocalShipping,
  CheckCircle,
  ArrowBack,
  Phone,
  LocationOn,
  Receipt,
  Lock
} from '@mui/icons-material';
import createAppTheme from '../theme/theme';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';
import MobileDrawer from '../components/MobileDrawer';

const BASE_URL = 'https://dhahabjw.com';

const CheckoutScreen = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [language, setLanguage] = useState(localStorage.getItem('lang') || 'en');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deliveryOption, setDeliveryOption] = useState('home_delivery');
  const [storePickupDetails, setStorePickupDetails] = useState({
    storeLocation: '',
    pickupContactPhone: ''
  });
  const [areas, setAreas] = useState([]);
  const [areasLoading, setAreasLoading] = useState(false);

  const t = translations[language];
  const theme = createAppTheme(true, language === 'ar');

  // Cart items state
  const [cartItems, setCartItems] = useState([]);

  // Modified form states with default N/A values
  const [shippingInfo, setShippingInfo] = useState({
    type: '',
    area: '',
    buildingNumber: '',
    streetNumber: '',
    block: '',
    avenue: '',
    floor: '',
    apartmentNumber: '',
    company: '',
    additionalDirections: '',
    phone: '',
    addressLabel: ''
  });

  const [paymentInfo, setPaymentInfo] = useState({
    method: 'cash'
  });

  const [errors, setErrors] = useState({});
  const [availableCategories, setAvailableCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchAreas();
  }, []);

  const fetchAreas = async () => {
    setAreasLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/area`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        // Sort areas alphabetically by name (English)
        const sortedAreas = (response.data.data || []).sort((a, b) => {
          const nameA = language === 'en' ? a.name.en : a.name.ar;
          const nameB = language === 'en' ? b.name.en : b.name.ar;
          return nameA.localeCompare(nameB);
        });
        setAreas(sortedAreas);
      } else {
        console.error('Failed to fetch areas');
      }
    } catch (error) {
      console.error('Error fetching areas:', error);
    } finally {
      setAreasLoading(false);
    }
  };

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/categories`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableCategories(data.data || []);
      } else {
        console.error('Failed to fetch categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    const fetchCartItems = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await axios.get('https://dhahabjw.com/api/cart', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success) {
          const items = response.data.data.items || [];
          const mappedItems = items.map((item) => ({
            id: item.id,
            productId: item.product.id,
            name: typeof item.product.name === 'object' ? item.product.name.en : item.product.name,
            category: item.product.category || 'Jewelry',
            karat: `${item.product.karat}K`,
            price: item.unitPrice,
            quantity: item.quantity,
            image: item.product.images?.find((img) => img.isPrimary)?.url || item.product.images?.[0]?.url
          }));
          setCartItems(mappedItems);
        }
      } catch (error) {
        console.error('Failed to fetch cart items:', error);
      }
    };

    fetchCartItems();
  }, []);

  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const selectedArea = areas.find(area => area._id === shippingInfo.area);
  const shipping = deliveryOption === 'store_pickup' ? 0 : (selectedArea?.shipping || 5);
  const total = subtotal + shipping;

  const steps = [
    t.steps.shippingInformation,
    t.steps.paymentMethod,
    t.steps.orderConfirmation
  ];

  // Helper function to determine which fields should be disabled based on address type
  const getFieldDisabledState = (fieldName) => {
    const { type } = shippingInfo;

    const fieldRules = {
      'House': {
        disabled: ['apartmentNumber', 'company', 'floor'],
        enabled: ['buildingNumber', 'streetNumber', 'block', 'avenue', 'additionalDirections', 'phone', 'addressLabel']
      },
      'Apartment': {
        disabled: ['company'],
        enabled: ['buildingNumber', 'streetNumber', 'block', 'avenue', 'floor', 'apartmentNumber', 'additionalDirections', 'phone', 'addressLabel']
      },
      'Office': {
        disabled: ['apartmentNumber', 'floor'],
        enabled: ['buildingNumber', 'streetNumber', 'block', 'avenue', 'company', 'additionalDirections', 'phone', 'addressLabel']
      }
    };

    if (!type || !fieldRules[type]) return false;
    return fieldRules[type].disabled.includes(fieldName);
  };

  // Modified address type onChange handler
  const handleAddressTypeChange = (newType) => {
    const updatedShippingInfo = { ...shippingInfo, type: newType };

    // Reset disabled fields to N/A when address type changes
    const fieldRules = {
      'House': ['apartmentNumber', 'company', 'floor'],
      'Apartment': ['company'],
      'Office': ['apartmentNumber', 'floor']
    };

    if (fieldRules[newType]) {
      fieldRules[newType].forEach(field => {
        if (field !== 'company' || newType !== 'Apartment') {
          updatedShippingInfo[field] = 'N/A';
        }
      });
    }

    setShippingInfo(updatedShippingInfo);
  };

  // Modified validation logic to handle N/A values
  const validateStep = () => {
    const newErrors = {};

    if (activeStep === 0) {
      if (!deliveryOption) newErrors.deliveryOption = t.selectDeliveryOption;

      if (deliveryOption === 'home_delivery') {
        if (!shippingInfo.type) newErrors.type = t.addressTypeRequired;
        if (!shippingInfo.area) newErrors.area = t.areaRequired;
        if (!shippingInfo.phone) newErrors.phone = t.phoneRequired;
        if (!shippingInfo.block) newErrors.block = t.blockRequired;
        if (!shippingInfo.streetNumber) newErrors.streetNumber = t.streetNumberRequired;

        const phonePattern = /^[0-9]{7,15}$/;
        if (shippingInfo.phone && !phonePattern.test(shippingInfo.phone)) {
          newErrors.phone = t.invalidPhoneFormat;
        }

        // Only validate apartment number if it's enabled and not N/A
        if (shippingInfo.type === 'Apartment' &&
          !getFieldDisabledState('apartmentNumber') &&
          (!shippingInfo.apartmentNumber || shippingInfo.apartmentNumber === 'N/A')) {
          newErrors.apartmentNumber = t.apartmentNumberRequired;
        }
      } else if (deliveryOption === 'store_pickup') {
        if (!storePickupDetails.storeLocation) newErrors.storeLocation = t.selectStoreLocation;
        if (!storePickupDetails.pickupContactPhone) newErrors.pickupContactPhone = t.pickupContactRequired;

        const phonePattern = /^[0-9]{7,15}$/;
        if (storePickupDetails.pickupContactPhone && !phonePattern.test(storePickupDetails.pickupContactPhone)) {
          newErrors.pickupContactPhone = t.invalidPhoneFormat;
        }
      }
    }

    if (activeStep === 2) {
      if (!termsAccepted) {
        newErrors.termsAccepted = t.termsRequired || 'You must accept the terms and conditions';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    if (activeStep === 2) {
      setTermsAccepted(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!validateStep()) return;

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const orderData = {
        items: cartItems.map(item => ({
          productId: item.productId,
          quantity: item.quantity
        })),
        deliveryOption: deliveryOption,
        paymentMethod: paymentInfo.method === 'cash' ? 'cod' :
          paymentInfo.method === 'tabby' ? 'tabby' : 'payment_link'
      };

      if (deliveryOption === 'home_delivery') {
        orderData.shippingAddress = shippingInfo;
      } else if (deliveryOption === 'store_pickup') {
        orderData.storePickupDetails = storePickupDetails;
      }

      const response = await axios.post(
        'https://dhahabjw.com/api/orders',
        orderData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setOrderNumber(response.data.data.orderNumber);
        setOrderComplete(true);
      } else {
        alert(t.orderPlacementFailed);
      }
    } catch (error) {
      console.error('Order placement error:', error);
      alert(t.orderPlacementError);
    } finally {
      setLoading(false);
    }
  };

  const OrderSummaryCard = () => (
    <Card
      sx={{
        position: 'sticky',
        top: 20,
        background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.8), rgba(30, 30, 30, 0.8))',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(212, 175, 55, 0.2)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}
    >
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
          {t.orderSummary}
        </Typography>
        
        {cartItems.map((item) => (
          <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">{item.name}</Typography>
            <Typography variant="body2">
              {item.price.toFixed(3)} KWD × {item.quantity}
            </Typography>
          </Box>
        ))}

        <Divider sx={{ my: 2, bgcolor: 'rgba(212, 175, 55, 0.2)' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">{t.subtotal}</Typography>
          <Typography variant="body2">{subtotal.toFixed(3)} KWD</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">{t.shipping}</Typography>
          <Typography variant="body2">
            {shipping === 0 ? 'FREE' : `${shipping.toFixed(3)} KWD`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">{t.delivery}</Typography>
          <Typography variant="body2">
            {deliveryOption === 'store_pickup' ? t.storePickupOption : t.homeDeliveryOption}
          </Typography>
        </Box>

        <Divider sx={{ my: 2, bgcolor: 'rgba(212, 175, 55, 0.2)' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{t.total}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {total.toFixed(3)} KWD
          </Typography>
        </Box>

        <Alert
          severity="info"
          icon={<Lock />}
          sx={{
            mt: 2,
            bgcolor: 'rgba(212, 175, 55, 0.1)',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            '& .MuiAlert-message': { color: 'text.primary' }
          }}
        >
          {t.paymentSecure}
        </Alert>
      </CardContent>
    </Card>
  );

  if (orderComplete) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
          <AppHeader language={language} setLanguage={setLanguage} t={t} />
          <Container maxWidth="md" sx={{ py: 8 }}>
            <Card
              sx={{
                textAlign: 'center',
                p: 4,
                background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.8), rgba(30, 30, 30, 0.8))',
                border: '1px solid rgba(212, 175, 55, 0.2)'
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  color: '#000',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <CheckCircle sx={{ fontSize: 50 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                {t.orderPlacedSuccess}
              </Typography>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t.orderNumber}: {orderNumber}
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                {deliveryOption === 'home_delivery' ? t.orderDeliveryMessage : t.thankYouMessage}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => window.location.href = '/'}
                  sx={{ mr: 2 }}
                >
                  {t.continueShoppingBtn}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => window.location.href = '/orders'}
                >
                  {t.viewOrdersBtn}
                </Button>
              </Box>
            </Card>
          </Container>
          <AppFooter language={language} t={t} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppHeader language={language} setLanguage={setLanguage} t={t} />
        <MobileDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          language={language}
          setLanguage={setLanguage}
          t={t}
        />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.8), rgba(30, 30, 30, 0.8))',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(212, 175, 55, 0.2)'
                }}
              >
                <CardContent>
                  <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label, index) => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>

                  {activeStep === 0 && (
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: 'primary.main' }}>
                        {t.deliveryInformation}
                      </Typography>

                      <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend" sx={{ mb: 2 }}>
                          {t.deliveryOption}
                        </FormLabel>
                        <RadioGroup
                          value={deliveryOption}
                          onChange={(e) => setDeliveryOption(e.target.value)}
                          sx={{ mb: 3 }}
                        >
                          <FormControlLabel
                            value="home_delivery"
                            control={<Radio />}
                            label={
                              <Box>
                                <Typography variant="body1">{t.homeDeliveryOption}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {t.homeDeliveryDesc}
                                </Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            value="store_pickup"
                            control={<Radio />}
                            label={
                              <Box>
                                <Typography variant="body1">{t.storePickupOption}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {t.storePickupDesc}
                                </Typography>
                              </Box>
                            }
                          />
                        </RadioGroup>
                      </FormControl>

                      {errors.deliveryOption && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {errors.deliveryOption}
                        </Alert>
                      )}

                      {deliveryOption === 'home_delivery' && (
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: 'primary.main' }}>
                            {t.shippingAddress}
                          </Typography>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                select
                                label={`${t.addressType} *`}
                                value={shippingInfo.type}
                                onChange={(e) => handleAddressTypeChange(e.target.value)}
                                error={!!errors.type}
                                helperText={errors.type}
                                SelectProps={{ native: true }}
                              >
                                <option value="">{`${t.addressType} *`}</option>
                                <option value="Apartment">{t.apartment}</option>
                                <option value="House">{t.house}</option>
                                <option value="Office">{t.office}</option>
                              </TextField>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              {/* UPDATED: Replace Select with Autocomplete */}
                              <Autocomplete
                                fullWidth
                                options={areas}
                                getOptionLabel={(option) => {
                                  const areaName = language === 'en' ? option.name.en : option.name.ar;
                                  return `${areaName} (${option.shipping.toFixed(3)} KWD)`;
                                }}
                                value={areas.find(area => area._id === shippingInfo.area) || null}
                                onChange={(event, newValue) => {
                                  setShippingInfo({ ...shippingInfo, area: newValue?._id || '' });
                                }}
                                loading={areasLoading}
                                disabled={areasLoading}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={`${t.area} *`}
                                    error={!!errors.area}
                                    helperText={errors.area}
                                    InputProps={{
                                      ...params.InputProps,
                                      startAdornment: (
                                        <>
                                          <InputAdornment position="start">
                                            <LocationOn sx={{ color: 'primary.main' }} />
                                          </InputAdornment>
                                          {params.InputProps.startAdornment}
                                        </>
                                      ),
                                      endAdornment: (
                                        <>
                                          {areasLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    }}
                                  />
                                )}
                                isOptionEqualToValue={(option, value) => option._id === value?._id}
                                noOptionsText={t.noAreasFound || 'No areas found'}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label={`${t.block} *`}
                                value={shippingInfo.block}
                                onChange={(e) => setShippingInfo({ ...shippingInfo, block: e.target.value })}
                                error={!!errors.block}
                                helperText={errors.block}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label={`${t.streetNumber} *`}
                                value={shippingInfo.streetNumber}
                                onChange={(e) => setShippingInfo({ ...shippingInfo, streetNumber: e.target.value })}
                                error={!!errors.streetNumber}
                                helperText={errors.streetNumber}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label={t.avenue}
                                value={shippingInfo.avenue}
                                onChange={(e) => setShippingInfo({ ...shippingInfo, avenue: e.target.value })}
                                disabled={getFieldDisabledState('avenue')}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label={t.buildingNumber}
                                value={shippingInfo.buildingNumber}
                                onChange={(e) => setShippingInfo({ ...shippingInfo, buildingNumber: e.target.value })}
                                disabled={getFieldDisabledState('buildingNumber')}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label={t.floor}
                                value={shippingInfo.floor}
                                onChange={(e) => setShippingInfo({ ...shippingInfo, floor: e.target.value })}
                                disabled={getFieldDisabledState('floor')}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label={t.apartmentNumber}
                                value={shippingInfo.apartmentNumber}
                                onChange={(e) => setShippingInfo({ ...shippingInfo, apartmentNumber: e.target.value })}
                                disabled={getFieldDisabledState('apartmentNumber')}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label={t.company}
                                value={shippingInfo.company}
                                onChange={(e) => setShippingInfo({ ...shippingInfo, company: e.target.value })}
                                disabled={getFieldDisabledState('company')}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label={t.additionalDirections}
                                value={shippingInfo.additionalDirections}
                                onChange={(e) => setShippingInfo({ ...shippingInfo, additionalDirections: e.target.value })}
                                disabled={getFieldDisabledState('additionalDirections')}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label={`${t.phoneNumber} *`}
                                value={shippingInfo.phone}
                                onChange={(e) => setShippingInfo({ ...shippingInfo, phone: e.target.value })}
                                error={!!errors.phone}
                                helperText={errors.phone}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Phone />
                                    </InputAdornment>
                                  )
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label={t.addressLabel}
                                value={shippingInfo.addressLabel}
                                onChange={(e) => setShippingInfo({ ...shippingInfo, addressLabel: e.target.value })}
                                disabled={getFieldDisabledState('addressLabel')}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                      {deliveryOption === 'store_pickup' && (
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: 'primary.main' }}>
                            {t.storePickupDetails}
                          </Typography>

                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label={`${t.phoneNumber} *`}
                                value={storePickupDetails.pickupContactPhone}
                                onChange={(e) => setStorePickupDetails({ ...storePickupDetails, pickupContactPhone: e.target.value })}
                                error={!!errors.pickupContactPhone}
                                helperText={errors.pickupContactPhone || t.pickupPhoneHelper}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Phone />
                                    </InputAdornment>
                                  )
                                }}
                              />
                            </Grid>
                          </Grid>

                          <Alert severity="info" sx={{ mt: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {t.storePickupInfo}
                            </Typography>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                              <li>{t.pickupReadyTime}</li>
                              <li>{t.pickupNotification}</li>
                              <li>{t.pickupIdRequired}</li>
                              <li>{t.pickupFreeCharge}</li>
                            </ul>
                          </Alert>
                        </Box>
                      )}
                    </Box>
                  )}

                  {activeStep === 1 && (
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: 'primary.main' }}>
                        {t.paymentMethod}
                      </Typography>

                      <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend" sx={{ mb: 2 }}>
                          {t.selectPaymentMethod}
                        </FormLabel>
                        <RadioGroup
                          value={paymentInfo.method}
                          onChange={(e) => setPaymentInfo({ ...paymentInfo, method: e.target.value })}
                        >
                          <FormControlLabel
                            value="tabby"
                            control={<Radio />}
                            label={t.tabbyLink}
                          />
                          <FormControlLabel
                            value="payment_link"
                            control={<Radio />}
                            label={t.paymentLink}
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  )}

                  {activeStep === 2 && (
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: 'primary.main' }}>
                        {t.orderConfirmation}
                      </Typography>

                      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                          {t.deliveryInformation}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {deliveryOption === 'home_delivery' ? 'Home Delivery' : 'Store Pickup'}
                        </Typography>

                        {deliveryOption === 'home_delivery' ? (
                          <Box>
                            {shippingInfo.addressLabel && (
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {shippingInfo.addressLabel}
                              </Typography>
                            )}
                            <Typography variant="body2">
                              {shippingInfo.type} – {areas.find(a => a._id === shippingInfo.area)?.name[language] || shippingInfo.area}
                            </Typography>
                            <Typography variant="body2">
                              {t.block}: {shippingInfo.block}
                            </Typography>
                            <Typography variant="body2">
                              {t.streetNumber}: {shippingInfo.streetNumber}
                            </Typography>
                            {shippingInfo.avenue && (
                              <Typography variant="body2">
                                {t.avenue}: {shippingInfo.avenue}
                              </Typography>
                            )}
                            {shippingInfo.buildingNumber && (
                              <Typography variant="body2">
                                {t.buildingNumber}: {shippingInfo.buildingNumber}
                              </Typography>
                            )}
                            {shippingInfo.floor && (
                              <Typography variant="body2">
                                {t.floor}: {shippingInfo.floor}
                              </Typography>
                            )}
                            {shippingInfo.apartmentNumber && (
                              <Typography variant="body2">
                                {t.apartmentNumber}: {shippingInfo.apartmentNumber}
                              </Typography>
                            )}
                            {shippingInfo.company && (
                              <Typography variant="body2">
                                {t.company}: {shippingInfo.company}
                              </Typography>
                            )}
                            {shippingInfo.additionalDirections && (
                              <Typography variant="body2">
                                {t.additionalDirections}: {shippingInfo.additionalDirections}
                              </Typography>
                            )}
                            <Typography variant="body2">
                              {t.phoneNumber}: {shippingInfo.phone}
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            <Typography variant="body2">
                              {t.storeLocation}: {storePickupDetails.storeLocation}
                            </Typography>
                            <Typography variant="body2">
                              {t.phoneNumber}: {storePickupDetails.pickupContactPhone}
                            </Typography>
                          </Box>
                        )}
                      </Card>

                      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                          {t.selectPaymentMethod}
                        </Typography>
                        <Typography variant="body2">
                          {paymentInfo.method === 'cash' && 'Cash on Delivery'}
                          {paymentInfo.method === 'tabby' && 'Tabby Link'}
                          {paymentInfo.method === 'payment_link' && 'Payment Link'}
                        </Typography>
                      </Card>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {t.agreeToTerms || 'I agree to the'}{' '}
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{ color: 'primary.main', cursor: 'pointer' }}
                              onClick={() => window.open('/terms-and-conditions', '_blank')}
                            >
                              {t.termsAndConditions || 'Terms and Conditions'}
                            </Typography>
                          </Typography>
                        }
                        sx={{ mb: 2 }}
                      />

                      {errors.termsAccepted && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {errors.termsAccepted}
                        </Alert>
                      )}

                      <Typography variant="body2" color="text.secondary">
                        {t.termsAgreement} {deliveryOption === 'home_delivery' ? t.orderDeliveryMessage : t.readyForPickup}.
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    <Button
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      startIcon={<ArrowBack />}
                    >
                      {t.back}
                    </Button>

                    {activeStep === steps.length - 1 ? (
                      <Button
                        variant="contained"
                        onClick={handlePlaceOrder}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <ShoppingCart />}
                      >
                        {loading ? t.processing : t.placeOrder}
                      </Button>
                    ) : (
                      <Button variant="contained" onClick={handleNext}>
                        {t.next}
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              <OrderSummaryCard />
            </Grid>
          </Grid>
        </Container>
        <AppFooter language={language} t={t} />
      </Box>
    </ThemeProvider>
  );
};

export default CheckoutScreen;
